import React, { useCallback, useMemo } from "react"
import { Box, Button, SearchIcon } from "@carrotfertility/carotene-core"
import { CarrotPlanMediaTypeId, CarrotPlanMediaTypes } from "./MediaTypes"
import { StepOptionSelect } from "./StepOptionSelect"
import { MediaOptionFields, CarrotPlanFormOption, TopicOptionFields } from "./hooks/useCarrotPlanMappings"
import { addDisabledMessageToDuplicates, ItemType } from "./CarrotPlanStepCarotene"
import { useFormContext, useWatch } from "@carrotfertility/carotene-core-x"
import { FieldName } from "./CarrotPlanFormTypes"
import { getAutocompleteMediaItemOptions } from "./utils/autocompleteTransformations"

const StepNamePlaceholdersByReplacement = {
  [CarrotPlanMediaTypes[CarrotPlanMediaTypeId.Article].replacement]: "Select article...",
  [CarrotPlanMediaTypes[CarrotPlanMediaTypeId.Video].replacement]: "Select video...",
  [CarrotPlanMediaTypes[CarrotPlanMediaTypeId.Guide].replacement]: "Select guide...",
  [CarrotPlanMediaTypes[CarrotPlanMediaTypeId.Group].replacement]: "Select group..."
}

type ContentfulItemDropdownsProps = {
  options: CarrotPlanFormOption<TopicOptionFields>[]
  topicSelection: CarrotPlanFormOption<TopicOptionFields>
  contentSelection?: CarrotPlanFormOption<MediaOptionFields>
  topicName: string
  contentName: string
  replacement: string
  stepIndex: number
}

export function ContentfulItemDropdowns({
  options,
  topicSelection,
  contentSelection,
  topicName,
  contentName,
  replacement,
  stepIndex
}: ContentfulItemDropdownsProps): JSX.Element {
  const stepUrl = contentSelection ? contentSelection?.optionFields.link : topicSelection?.optionFields?.link || ""
  const steps = useWatch({ name: FieldName.steps })
  const { setValue } = useFormContext()

  const getUpdatedMediaOptionsInSelect = useCallback(
    function <T extends MediaOptionFields>(
      options: CarrotPlanFormOption<T>[],
      itemType: ItemType
    ): CarrotPlanFormOption<T>[] {
      return addDisabledMessageToDuplicates<T>({
        options,
        steps: steps,
        stepIndex: stepIndex,
        itemType
      })
    },
    [steps, stepIndex]
  )
  const mediaItemOptions = useMemo(() => getAutocompleteMediaItemOptions(topicSelection?.optionFields?.options), [
    topicSelection
  ])

  return (
    <Box display="flex" alignItems="center" flexDirection="row" gap={(theme) => theme.spacing(theme.tokens.spacing.xs)}>
      <Box width="100%" display="flex" flexDirection="column" gap={(theme) => theme.spacing(theme.tokens.spacing.xs)}>
        <StepOptionSelect
          options={options}
          name={topicName}
          onInputChange={(event) => {
            // if topic is changed, clear the content except onReorder (event is null)
            if (event) {
              setValue(contentName, null)
            }
          }}
          placeholder="Select topic..."
          rules={{ required: "Required" }}
        />
        <StepOptionSelect
          name={contentName}
          placeholder={StepNamePlaceholdersByReplacement[replacement]}
          rules={{ required: "Required" }}
          disabled={!topicSelection}
          options={getUpdatedMediaOptionsInSelect(mediaItemOptions, "content") || []}
        />
      </Box>
      <Box>
        <Button component="a" target="_blank" href={stepUrl} startIcon={<SearchIcon />} disabled={!stepUrl}>
          View
        </Button>
      </Box>
    </Box>
  )
}
